import { useState } from 'react'
import { Stack, Typography } from '@mui/material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { getBomLibraryDetail, searchBomLibrary } from '../bomLibrary/bomLibraryApi'

/** PO 2026-09-10: a workflow can only be linked to APPROVED versions. */
export const LINKABLE_INDEX_VALUE = 'APPROVED'

/**
 * Adds `currentIndexValue = APPROVED` to the table's own column filters (the three parallel arrays
 * of `RequestPagination`), keeping every filter the user typed in the table.
 * @param {object} pagination the RequestPagination built by SmartMateriaUITable
 * @returns {object} a new RequestPagination with the extra column filter
 */
export const withApprovedOnlyFilter = (pagination = {}) => ({
  ...pagination,
  columnFilters: [...(pagination.columnFilters || []), 'currentIndexValue'],
  columnFilterValues: [...(pagination.columnFilterValues || []), LINKABLE_INDEX_VALUE],
  columnFilterOperators: [...(pagination.columnFilterOperators || []), '']
})

/**
 * LinkBomTable — the library listing inside the link dialog, built like `WorkflowTable`: one
 * paginated `SmartMateriaUITable` over N1 (the table's own RequestPagination, search included), a
 * row click selects the BOM **at its current version** (RN-05.1/D12: the highest numbered one), and
 * the row's accordion lists the other APPROVED versions of that BOM (N2) so one of them can be
 * picked with a click instead. Only APPROVED versions are listed (PO 2026-09-10), which is why no
 * index column is shown: every row is APPROVED by construction.
 *
 * @param {object} props
 * @param {function({bomId:number, code:string, name:string, versionNumber:number, indexValue:string})} props.onBomSelected
 */
const LinkBomTable = ({ onBomSelected }) => {
  const { t_ } = useSmartTranslation()

  const selectCurrentVersion = (row) => onBomSelected({
    bomId: row.currentBomId,
    code: row.code,
    name: row.name,
    versionNumber: row.currentVersionNumber,
    indexValue: row.currentIndexValue
  })

  const selectVersion = (bom) => (version) => onBomSelected({
    bomId: version.bomId,
    code: bom.code,
    name: bom.name,
    versionNumber: version.versionNumber,
    indexValue: version.indexValue
  })

  const columns = [
    { name: t_('Code'), field: 'code' },
    { name: t_('Name'), field: 'name' },
    { name: t_('Description'), field: 'description' },
    { name: t_('Version'), field: 'currentVersionNumber' },
    { name: t_('Created'), field: 'currentCreatedAt', type: 'datetime' },
    { name: t_('Components'), field: 'componentCount' }
  ]

  const versionColumns = [
    { name: t_('Version'), field: 'versionNumber' },
    { name: t_('Created'), field: 'createdAt', type: 'datetime' },
    { name: t_('Created by'), field: 'createdBy' },
    { name: t_('Components'), field: 'componentCount' }
  ]

  const actions = {
    accordion: {
      // Same idea as WorkflowTable's "moreVersions": the accordion only makes sense with history.
      visibilityButton: (row) => Number(row.versionCount) > 1,
      render: (row) => (
        <SmartMateriaUITable
          columns={versionColumns}
          // N2 brings every version, newest first; only the APPROVED ones are offered (PO 2026-09-10).
          dataFetch={async () => (((await getBomLibraryDetail(row.code))?.versions) || []).filter((version) => version.indexValue === LINKABLE_INDEX_VALUE)}
          onRowClick={selectVersion(row)}
          primaryKeys={['bomId']}
          searchAndPaginationVisible='none'
          tablePageSize={Number.MAX_SAFE_INTEGER}
          sticky={false}
        />
      )
    }
  }

  // PO 2026-09-10: only APPROVED versions can be linked. Sun (PE-2) dropped N1's dedicated
  // `indexValues` field: the filter travels as a regular column filter on `currentIndexValue` (the
  // CURRENT version's index), appended to whatever column filters the table itself is sending.
  // SmartMateriaUITable keeps the accordion open/closed state per `row.id`; N1 rows have no `id`
  // (one row per code), so without one every accordion would toggle together.
  const dataFetch = async (pagination) => {
    const page = await searchBomLibrary(withApprovedOnlyFilter(pagination))
    if (page && Array.isArray(page.content)) {
      return { ...page, content: page.content.map((row) => ({ ...row, id: row.id ?? row.currentBomId ?? row.code })) }
    }
    return page
  }

  return (
    <SmartMateriaUITable
      columns={columns}
      actions={actions}
      dataFetch={dataFetch}
      onRowClick={selectCurrentVersion}
      primaryKeys={['code']}
      pageable
      sticky={false}
      sortOptions={{ default: { field: 'code', direction: 1 } }}
    />
  )
}

/**
 * LinkBomDialog — S71 (HU-05/CA5.1, RN-05.1, RN-05.2, D3/D12): picks the version of a library BOM
 * the workflow being edited is to be linked to, with the look and the selection flow of the rest of
 * the editor's dialogs (`WorkflowHeader` → `WorkflowTable`): a `SmartDialog` whose content is a
 * `SmartMateriaUITable`, the row click selects, and the dialog's accept button ("Link") confirms.
 *
 * Corrección PO 2026-09-09: this dialog PERSISTS NOTHING. In the workflow editor everything is
 * created and edited in the interface and written by the general "Save"; linking behaves the same
 * way, so the choice becomes a pending change of the editor's model (`workflow.pendingBomLink`) and
 * N16 runs inside `saveWorkflow`. Whatever the link would discard (an embedded BOM, an existing
 * link) is confirmed by `WorkflowBomLinkHeader`, the component that knows the workflow's current
 * BOM — this one only reports WHICH version was chosen.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {function({bomId:number, code:string, name:string, versionNumber:number, indexValue:string})} props.onSelected
 * called with the chosen version when the user accepts
 * @param {function()} props.onCancel
 */
const LinkBomDialog = ({ open = false, onSelected = () => {}, onCancel = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [selected, setSelected] = useState({})

  const accept = () => {
    if (!selected.bomId) {
      return
    }
    onSelected(selected)
  }

  return (
    <SmartDialog
      setOpen={open}
      title={{ render: <Typography variant='h6'>{t_('Link library BOM')}</Typography> }}
      acceptLabel='Link'
      acceptCallback={accept}
      cancelCallback={onCancel}
      autoClose={false}
      paperWidth='70rem'
      renderComponent={
        <Stack spacing={1} sx={{ textAlign: 'left' }} data-testid='link-bom-dialog'>
          {/* The selected row is the one the table highlights, as in WorkflowTable: no caption. */}
          <LinkBomTable onBomSelected={setSelected} />
        </Stack>
      }
    />
  )
}

export default LinkBomDialog
export { LinkBomTable }
